'use client';

import Link from 'next/link';
import { usePathname } from 'next/navigation';
import { useTranslations } from 'next-intl';
import { RefObject, useMemo, useState } from 'react';

import { clearSession } from '@/features/travel/lib/utils';
import { MENU_ITEMS } from '@/shared/model/constants/home-page';

import PcNavigationItem from './PcNavigationItem';

interface PcNavigationProps {
  submenuRef: RefObject<HTMLUListElement | null>;
  onSubmenuEnter: () => void;
  height: number;
  isHovered: boolean;
  setIsHovered: (isHovered: boolean) => void;
}

const defaultSubMenu = 'font-medium text-secondary-black';
const activeSubMenu = 'font-semibold text-primary-600';

const PcNavigation = ({
  submenuRef,
  onSubmenuEnter,
  height,
  isHovered,
  setIsHovered,
}: PcNavigationProps) => {
  const t = useTranslations();
  const [hoveredIndex, setHoveredIndex] = useState<number | null>(null);
  const pathname = usePathname();
  const withOutLocalePathname = useMemo(() => {
    return pathname.substring(6);
  }, [pathname]);
  const getFontClass = (href: string) => {
    if (withOutLocalePathname === href.split('#')[0]) {
      return activeSubMenu;
    }
    return defaultSubMenu;
  };
  return (
    <nav className={`${isHovered ? 'cursor-pointer' : ''}`} id="top">
      <ul
        className={`flex transition-all duration-400 ${
          isHovered
            ? 'gap-[clamp(65px,calc(4.6875vw+5px),95px)]'
            : 'gap-[clamp(60px,calc(3.125vw+20px),80px)]'
        }`}
      >
        {MENU_ITEMS.map((item, index) => (
          <li
            key={item.id}
            className={`relative transition-all duration-400 whitespace-nowrap min-w-max ${
              index === 0 && isHovered ? '-translate-x-[clamp(8px,calc(1.25vw-8px),16px)]' : ''
            }`}
            onMouseLeave={() => setHoveredIndex(null)}
          >
            <div
              className="h-[100px] flex items-center"
              onMouseEnter={() => {
                onSubmenuEnter();
                setIsHovered(true);
                setHoveredIndex(index);
              }}
            >
              <PcNavigationItem
                item={{
                  ...item,
                  href: item.href,
                  label: t(item.translationKey),
                }}
              />
            </div>
            {/* 하단 파란 선은 해당 메뉴 항목에만 보임 */}
            <div
              className={`absolute bottom-0 left-1/2 -translate-x-1/2 w-[150%] h-[3px] bg-primary-600 transition-transform duration-700 ease-in-out ${
                hoveredIndex === index ? 'scale-x-100' : 'scale-x-0'
              }`}
            />
            {item.submenu && (
              <ul
                onMouseEnter={() => {
                  if (isHovered) {
                    setHoveredIndex(index);
                  }
                }}
                ref={submenuRef}
                className="absolute top-[130px] left-1/2 -translate-x-1/2 flex flex-col items-center space-y-4"
              >
                {item.submenu.map((subItem) => (
                  <li
                    key={subItem.id}
                    className={`transition-opacity duration-300 ${
                      height > 0 ? 'opacity-100 delay-[300ms]' : 'opacity-0 invisible'
                    }`}
                  >
                    <Link
                      href={subItem.href}
                      target={subItem?.target ?? '_self'}
                      rel={subItem?.rel ?? ''}
                      onClick={clearSession}
                      className={`block text-[clamp(12px,calc(1.25vw-7px),17px)] hover:font-semibold hover:text-primary-600 whitespace-nowrap ${getFontClass(subItem.href)}`}
                    >
                      {t(subItem.translationKey)}
                    </Link>
                  </li>
                ))}
              </ul>
            )}
          </li>
        ))}
      </ul>
    </nav>
  );
};

export default PcNavigation;
